import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signInWithPhone, getEmbeddedSignupByPhone } from '../../../api/login.service';
import { useAuth } from '../../../authContext';
import { setCookie } from '../../../api/cookie';
import { Mixpanel } from '../../../mixpanel/MixpanelTrack';
import { EVENTS } from '../../../mixpanel/mixpanelEvent';
import { IoMdArrowBack, IoMdRefresh, IoMdCheckmarkCircle, IoMdCloseCircle } from 'react-icons/io';

function ModernVerification() {
  const [otp, setOtp] = useState<string>('');
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [success, setSuccess] = useState(false);
  
  const { email, login, setDisplayPhoneNumber, setVerifiedName } = useAuth();
  const navigate = useNavigate();

  const isMobile = (): boolean => {
    const userAgent = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|Windows Phone/i.test(userAgent);
  };

  const mobileDevice = isMobile();

  useEffect(() => {
    if (!email) {
      navigate('/signin');
    }
  }, [email]);

  useEffect(() => {
    if (mobileDevice) {
      Mixpanel.track(EVENTS.mobile_page_loaded, { requestEmail: email });
    } else {
      Mixpanel.track(EVENTS.desktop_page_loaded, { requestEmail: email });
    }
  }, [mobileDevice, email]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendCode = () => {
    if (resendCooldown > 0) return;
    
    setResendCooldown(60);
    setError('');
    console.log('Resending code...');
    // Implement resend logic here
  };

  const handleUnlockClick = async () => {
    if (otp.length !== 4) {
      setError('Please enter a valid 4-digit verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const loginType = sessionStorage.getItem("logintype");
      
      if (loginType === 'phone') {
        const phone = sessionStorage.getItem("phone");
        if (!phone) {
          throw new Error('Phone number not found');
        }

        const result = await signInWithPhone(phone, otp);
        
        if (result && 'accessToken' in result) {
          // Store the access token
          const accessToken = result.accessToken;
          
          // Complete the login first to store the token
          login(accessToken, { phoneNumber: phone });
          
          // Get embedded signup data to get user details
          try {
            const embeddedData = await getEmbeddedSignupByPhone(phone);
            
            if (embeddedData?.searchPhone) {
              sessionStorage.setItem("display_phone_number", embeddedData.searchPhone);
              setDisplayPhoneNumber(embeddedData.searchPhone);
            }
            
            if (embeddedData?.records && embeddedData.records.length > 0) {
              const record = embeddedData.records[0];
              
              if (record.wabaId) {
                sessionStorage.setItem("wabaId", record.wabaId);
              }
              
              if (record.verified_name) {
                sessionStorage.setItem("verified_name", record.verified_name);
                setVerifiedName(record.verified_name);
              }
              
              if (record.phoneNumberId) {
                sessionStorage.setItem("phoneNumberId", record.phoneNumberId);
              }
            }
          } catch (error: any) {
            console.error('Error fetching embedded signup data:', error);
            // Continue with login even if this fails
          }
          
          // Set cookies for tracking
          setCookie('user_id', phone, 1);
          setCookie('user_email', phone, 1);
          setCookie('user_name', phone, 1);
          
          // Track successful login
          Mixpanel.set({email: phone, name: phone}, phone);
          Mixpanel.track(EVENTS.successful_login, { requestEmail: phone, loginType: 'phone'});
          
          setSuccess(true);
          
          // Navigation is handled automatically by ProtectedRoute when isAuthenticated becomes true
        } else {
          throw new Error('Verification failed - invalid response');
        }
      } else {
        // Handle email/other login types
        const result = await signIn({ email, otp });
        
        if (result && 'accessToken' in result) {
          const accessToken = result.accessToken;
          
          login(accessToken, { email });
          
          // Set cookies for tracking
          setCookie('user_id', email, 1);
          setCookie('user_email', email, 1);
          setCookie('user_name', email, 1);
          
          Mixpanel.set({email: email, name: email}, email);
          Mixpanel.track(EVENTS.successful_login, { requestEmail: email, loginType: 'email'});
          
          setSuccess(true);
          
          setTimeout(() => {
            //navigate('/events/');
          }, 2000);
        } else {
          throw new Error('Verification failed - invalid response');
        }
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err?.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);
    setError('');
  };

  const handleBackClick = () => {
    navigate('/signin');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <IoMdCheckmarkCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Successful!</h2>
            <p className="text-gray-600 mb-4">Welcome to frontdesk24</p>
            <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex">
      {/* Left Side - Verification Form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-sm w-full">
          {/* Back Button */}
          <button
            onClick={handleBackClick}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
          >
            <IoMdArrowBack className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Back to Sign In</span>
          </button>

          {/* Logo */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Verify Your Account</h1>
            <p className="text-sm text-gray-600">
              We've sent a 4-digit verification code to
            </p>
            <p className="text-sm text-gray-900 font-semibold">{email}</p>
          </div>

          {/* OTP Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Enter Verification Code
            </label>
            <div className="flex justify-center space-x-2">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={otp[index] || ''}
                  onChange={(e) => {
                    const newOtp = otp.split('');
                    newOtp[index] = e.target.value;
                    const updatedOtp = newOtp.join('');
                    handleOtpChange(updatedOtp);
                    
                    // Auto-focus next input if current input has value
                    if (e.target.value && index < 3) {
                      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`) as HTMLInputElement;
                      if (nextInput) {
                        nextInput.focus();
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace to go to previous input
                    if (e.key === 'Backspace' && !otp[index] && index > 0) {
                      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`) as HTMLInputElement;
                      if (prevInput) {
                        prevInput.focus();
                      }
                    }
                  }}
                  data-index={index}
                  className="w-10 h-10 text-center text-lg font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              ))}
            </div>
            {error && (
              <div className="mt-4 flex items-center justify-center text-red-600">
                <IoMdCloseCircle className="w-5 h-5 mr-2" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>

          {/* Verify Button */}
          <button
            onClick={handleUnlockClick}
            disabled={otp.length !== 4 || isLoading}
            className={`w-full py-2.5 px-4 rounded-lg font-medium transition-all duration-200 mb-4 ${
              otp.length === 4 && !isLoading
                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Verifying...
              </div>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Resend Code */}
          <div className="text-center mb-4">
            <p className="text-xs text-gray-600 mb-1">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
              className={`text-xs font-medium transition-colors duration-200 ${
                resendCooldown > 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              {resendCooldown > 0 ? (
                `Resend in ${resendCooldown}s`
              ) : (
                <div className="flex items-center justify-center">
                  <IoMdRefresh className="w-3 h-3 mr-1" />
                  Resend Code
                </div>
              )}
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h3 className="text-xs font-semibold text-blue-900 mb-1">Need Help?</h3>
            <p className="text-xs text-blue-700">
              • Check your SMS messages for the verification code
              • Make sure you entered the correct phone number
              • The code expires in 10 minutes
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative z-10 flex items-center justify-center p-6">
          <div className="text-center text-white">
            <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <IoMdCheckmarkCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-3">Secure Verification</h2>
            <p className="text-lg text-blue-100 mb-6 max-w-md">
              Your account security is our priority. Verify your identity to access all features safely.
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
              <div className="text-center">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-xl">🔐</span>
                </div>
                <p className="text-xs text-blue-100">Secure</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-xl">⚡</span>
                </div>
                <p className="text-xs text-blue-100">Fast</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-xl">✅</span>
                </div>
                <p className="text-xs text-blue-100">Verified</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-xl">🛡️</span>
                </div>
                <p className="text-xs text-blue-100">Protected</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-10 w-16 h-16 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute top-1/2 right-20 w-12 h-12 bg-white bg-opacity-10 rounded-full"></div>
      </div>
    </div>
  );
}

export default ModernVerification;
